/**
 * The Squares desktop content column — defined here exactly once.
 *
 * Squares is a mobile-first surface that runs embedded in a DK sportsbook iframe
 * (see `./embedChrome.ts`). Below `md` it fills the frame; from `md` up it stops
 * growing and renders as a centred column.
 *
 * Six surfaces have to agree on that column, and they centre it two different ways:
 *
 *   in-flow overlays   `md:mx-auto` — MainLayout, HelpView, RulesView
 *   `position: fixed`  `md:mx-auto` — RulesPopup, PlayView's rules panel, the
 *                                     anchored FooterAdvert
 *
 * Both centre with auto margins, but they are still two constants: the fixed variant
 * additionally needs `w-full` and its own `max-width` handling (see below). A fixed
 * overlay re-derives the column from the viewport rather than from its parent, so if
 * the cap is edited in one place and not the others the footer ad and both rules popups
 * silently detach from the content. That is why the width is not written anywhere else
 * in the app.
 *
 * ⚠️ These must stay whole, literal class strings. Tailwind scans source statically
 * and never sees a class that was assembled at runtime, so do not build them by
 * concatenation, interpolation, or a lookup map.
 */

/**
 * In-flow surfaces: cap the width and centre with auto margins.
 * Compose as `w-full ${DESKTOP_COLUMN} …`.
 */
export const DESKTOP_COLUMN = 'md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1200px] md:mx-auto';

/**
 * `position: fixed` overlays: cap the width and centre on the viewport midpoint.
 * Pairs with `fixed inset-x-0` so the element is full-bleed below `md`.
 *
 * Centred with auto margins, NOT `left-1/2 + -translate-x-1/2`. An absolutely
 * positioned box with both `left` and `right` set, a `max-width`, and `auto` side
 * margins has its leftover space split equally between them — that is centring, and
 * it lives entirely in the box model.
 *
 * ⚠️ Do not "simplify" this back to a transform. Two of the three consumers animate:
 * PlayView's rules panel runs `rules-popup-slide-up` (`animation-fill-mode: forwards`)
 * and RulesPopup slides on `translate-y-full`. A keyframe that sets the `transform`
 * property replaces it wholesale, so it silently drops the `-50%` X offset — and with
 * `forwards` the clobber is permanent. That left the How-to-Play panel pinned with its
 * left edge on the viewport midpoint, hanging off the right of the screen.
 *
 * `w-full` is load-bearing — do not drop it. These overlays are `fixed inset-x-0`,
 * so both `left` and `right` are set. With `width: auto` that over-constrains the
 * box and the used width becomes `100vw − 50vw − 0 = 50vw`, then clamped by
 * `max-width` — i.e. `min(50vw, cap)`, while the content column is `min(100vw, cap)`.
 * At 1440px that is a 720px popup against a 1200px column. `width: 100%` resolves
 * the over-constraint in favour of the width, restoring `min(100vw, cap)` so the
 * overlay stays welded to the column at every breakpoint. Below `md` it is a no-op.
 */
export const DESKTOP_COLUMN_FIXED =
  'w-full md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1200px] md:mx-auto';

/**
 * The matchup band inside the hero header.
 *
 * The two team jerseys used to be anchored to the *column* edges, which is a mobile
 * rule wearing a desktop hat: on a phone the column is the viewport, so bleeding a
 * jersey 24px off each edge lands it right beside the centre logo. At `xl` the column
 * is 1200px, so the same rule stranded two 195px shirts ~400px out in the corners with
 * ~170px of each one cropped by the frame, around a wide empty middle.
 *
 * The fix is to give the matchup its own centred band and hang the jerseys off *that*.
 * At or below this width the band is the column and nothing changes — mobile is byte
 * -for-byte identical. Above it the jerseys stay the same distance from the logo they
 * have on a phone, and the gutter absorbs the growth instead.
 *
 * 720px is deliberately the `md` cap of DESKTOP_COLUMN above: the band is "the column,
 * frozen at the width the layout was designed for". The hero background image is NOT
 * banded — it stays full-bleed across the column.
 */
export const MATCHUP_BAND_PX = 720;

/**
 * Cap in-flow boards (the 10×10 grid) to the same band the jerseys sit on, and
 * centre them. Below this width the class is a no-op — the column is already
 * narrower — so mobile is unchanged. Above it the cells stay true squares at a
 * phone-like size instead of stretching into letterbox rows across 960/1200px.
 */
export const MATCHUP_BAND = 'md:max-w-[720px] md:mx-auto';

/**
 * The gutters either side of the column.
 *
 * The page used to be painted the same `#121212` as the column itself, separated
 * only by `md:shadow-2xl` — on a wide desktop that reads as a broken page rather
 * than a deliberate frame, because there is nothing to say where the app stops and
 * the background starts. A darker gutter plus a hairline edge is the whole fix.
 */
export const APP_GUTTER_BG = 'bg-[#0a0a0a]';

/** The column's own surface colour — must differ from APP_GUTTER_BG. */
export const APP_COLUMN_BG = 'bg-[#121212]';

/** Edge treatment applied to the column from `md` up, where gutters exist. */
export const DESKTOP_COLUMN_FRAME = 'md:shadow-2xl md:border-x md:border-[#262626]';

/**
 * Card lists (Contests, My Picks, Results).
 *
 * Below `md` this is a single stack and is visually identical to the `space-y-3`
 * it replaces. Above it, the cards flow into columns instead of stretching — a
 * contest card at 1200px is an unreadable letterbox row. Column counts are chosen
 * to keep each card near its mobile width: 720/2 ≈ 344, 960/2 ≈ 464, 1200/3 ≈ 389.
 */
export const CARD_LIST_GRID = 'grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3';

/**
 * Prose / single-artwork blocks inside a wide column.
 *
 * Rules and Help are body copy and one large aspect-ratio banner. Neither should
 * track the column: at 1200px the copy is unreadable and the 375/351 banner grows
 * to over 1100px tall. Cap them at a comfortable measure and centre instead.
 */
export const READABLE_CONTENT = 'w-full max-w-[560px] mx-auto';
